/**
 * D1: App Preferences (group, period)
 * Source: src/app/store.ts lines 375-428
 */

import { USE_BACKEND, STORAGE_KEY } from '../helpers'

export async function getGroup(): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_group`)
    if (!stored) return null
    return JSON.parse(stored)
  } catch {
    return null
  }
}

export async function setGroup(group: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(`${STORAGE_KEY}_group`, JSON.stringify(group))
  } catch {
    /* quota */
  }
}

export async function getPeriod(): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_period`)
    if (!stored) return null
    return JSON.parse(stored)
  } catch {
    return null
  }
}

export async function setPeriod(period: string): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.setItem(`${STORAGE_KEY}_period`, JSON.stringify(period))
  } catch {
    /* quota */
  }
}

export async function clearPeriod(): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    localStorage.removeItem(`${STORAGE_KEY}_period`)
  } catch {
    // silent
  }
}

/**
 * Parent "last viewed child" memory — parent-scoped.
 *
 * Stored as `Record<parentId, learnerId>` under a single key so multiple
 * parents sharing one device don't overwrite each other's selection.
 *
 * Used by ParentShellV2 to restore the previously viewed child on mount
 * for Multi-child Mara persona (W3-3, see PARENT_PORTAL_PLAN_2026.md).
 */
type ParentLastViewedMap = Record<string, string>

const PARENT_LAST_VIEWED_KEY = `${STORAGE_KEY}_parentLastViewedLearner`
// Legacy single-value key — read once on first access and migrate into the map.
const LEGACY_PARENT_LAST_VIEWED_KEY = 'meester_djay_parent_last_viewed_child'

function readParentLastViewedMap(): ParentLastViewedMap {
  try {
    const raw = localStorage.getItem(PARENT_LAST_VIEWED_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ParentLastViewedMap
    }
    return {}
  } catch {
    return {}
  }
}

function writeParentLastViewedMap(map: ParentLastViewedMap): void {
  try {
    localStorage.setItem(PARENT_LAST_VIEWED_KEY, JSON.stringify(map))
  } catch {
    /* quota */
  }
}

export async function getParentLastViewedLearner(
  parentId: string,
): Promise<string | null> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const map = readParentLastViewedMap()
    if (map[parentId]) return map[parentId]
    // One-time migration from legacy single-value key
    const legacy = localStorage.getItem(LEGACY_PARENT_LAST_VIEWED_KEY)
    if (legacy) {
      const next = { ...map, [parentId]: legacy }
      writeParentLastViewedMap(next)
      try {
        localStorage.removeItem(LEGACY_PARENT_LAST_VIEWED_KEY)
      } catch {
        // ignore
      }
      return legacy
    }
    return null
  } catch {
    return null
  }
}

export async function setParentLastViewedLearner(
  parentId: string,
  learnerId: string,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const map = readParentLastViewedMap()
    if (map[parentId] === learnerId) return
    writeParentLastViewedMap({ ...map, [parentId]: learnerId })
  } catch {
    /* quota */
  }
}

/**
 * Parent tip dismissals — parent-scoped (W4-2).
 *
 * Stored as `Record<parentId, Record<tipId, dismissedAtMs>>` under a single
 * key. A tip stays hidden for TIP_HIDE_WINDOW_MS (14 days) after dismissal,
 * then re-surfaces. Used by TipsPage (dismiss button + filter) and HomePage
 * (top-tip preview filter) so Coach Cas doesn't see the same tip every week.
 *
 * Undo: the SettingsPage-style sonner pattern is replicated — clearing a
 * dismissal restores the tip immediately.
 */
export const TIP_HIDE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000

type ParentTipDismissalsByParent = Record<string, Record<string, number>>

const PARENT_TIP_DISMISSALS_KEY = `${STORAGE_KEY}_parentTipDismissals`

function readParentTipDismissalsMap(): ParentTipDismissalsByParent {
  try {
    const raw = localStorage.getItem(PARENT_TIP_DISMISSALS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as ParentTipDismissalsByParent
    }
    return {}
  } catch {
    return {}
  }
}

function writeParentTipDismissalsMap(map: ParentTipDismissalsByParent): void {
  try {
    localStorage.setItem(PARENT_TIP_DISMISSALS_KEY, JSON.stringify(map))
  } catch {
    /* quota */
  }
}

/**
 * Returns the active (non-expired) dismissals for a parent.
 * Expired entries (older than TIP_HIDE_WINDOW_MS) are filtered out and the
 * underlying store is cleaned up lazily.
 */
export async function getParentTipDismissals(
  parentId: string,
): Promise<Record<string, number>> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const map = readParentTipDismissalsMap()
    const forParent = map[parentId] || {}
    const now = Date.now()
    const active: Record<string, number> = {}
    let didExpire = false
    for (const [tipId, ts] of Object.entries(forParent)) {
      if (typeof ts === 'number' && now - ts < TIP_HIDE_WINDOW_MS) {
        active[tipId] = ts
      } else {
        didExpire = true
      }
    }
    if (didExpire) {
      const next = { ...map, [parentId]: active }
      writeParentTipDismissalsMap(next)
    }
    return active
  } catch {
    return {}
  }
}

export async function setParentTipDismissed(
  parentId: string,
  tipId: string,
  timestampMs: number = Date.now(),
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const map = readParentTipDismissalsMap()
    const forParent = { ...(map[parentId] || {}), [tipId]: timestampMs }
    writeParentTipDismissalsMap({ ...map, [parentId]: forParent })
  } catch {
    /* quota */
  }
}

export async function clearParentTipDismissal(
  parentId: string,
  tipId: string,
): Promise<void> {
  if (USE_BACKEND) {
    throw new Error('Backend not implemented')
  }
  try {
    const map = readParentTipDismissalsMap()
    const forParent = map[parentId]
    if (!forParent || !(tipId in forParent)) return
    const next = { ...forParent }
    delete next[tipId]
    writeParentTipDismissalsMap({ ...map, [parentId]: next })
  } catch {
    /* quota */
  }
}

export function isTipDismissed(
  dismissals: Record<string, number>,
  tipId: string,
  now: number = Date.now(),
): boolean {
  const ts = dismissals[tipId]
  return typeof ts === 'number' && now - ts < TIP_HIDE_WINDOW_MS
}
